import React from 'react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const features = [
    {
      title: 'Creating Charts',
      description: 'Click on any measure to add or edit chords. Use the chord palette or type directly. Add lyrics below each measure.',
      icon: '✏️'
    },
    {
      title: 'Playing & Transport',
      description: 'Use the play button to hear your chart. Set BPM, choose instrument timbre, and enable metronome in the transport controls.',
      icon: '▶️'
    },
    {
      title: 'MIDI Input',
      description: 'Connect a MIDI keyboard to input chords by playing them. Open Settings & Tools to select your device.',
      icon: '🎹'
    },
    {
      title: 'Transpose',
      description: 'Use the +/- buttons in Edition mode or Gig mode to transpose your entire chart up or down by semitones.',
      icon: '🎵'
    },
    {
      title: 'Save & Load',
      description: 'Save your charts with a custom name. Your charts are stored locally and synced to Google Drive when signed in.',
      icon: '💾'
    },
    {
      title: 'Setlists',
      description: 'Organize your charts into setlists for gigs. Create, edit, and reorder songs for easy performance navigation.',
      icon: '📋'
    },
    {
      title: 'Gig Mode',
      description: 'Clean, distraction-free view optimized for live performance. Navigate songs with arrows, transpose on the fly.',
      icon: '🎸'
    },
    {
      title: 'Jam Session',
      description: 'Host or join collaborative sessions. Share charts in real-time with other musicians via WebRTC.',
      icon: '🎺'
    },
    {
      title: 'Annotations',
      description: 'Add repeats, codas, segnos, and other musical notations. Click the icons in the measure toolbar.',
      icon: '🎼'
    },
    {
      title: 'Chord Inspector',
      description: 'Click any chord to see its notes displayed on a piano keyboard. Great for learning voicings.',
      icon: '🔍'
    },
    {
      title: 'Cloud Sync',
      description: 'Sign in with Google to automatically sync charts across all your devices via Google Drive.',
      icon: '☁️'
    },
    {
      title: 'Keyboard Shortcuts',
      description: 'Space: Play/Pause | Tab: Next measure | Shift+Tab: Previous measure | Ctrl+S: Save',
      icon: '⌨️'
    }
  ];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[320] p-4"
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        style={{ backgroundColor: '#161616' }}
        onClick={e => e.stopPropagation()}
      >
        <header className="flex justify-between items-center p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Quick Start Guide</h2>
            <p className="text-gray-400 text-sm mt-1">Get the most out of Chord Notator — <a href="https://blog.chordnotator.com" target="_blank" rel="noopener" className="text-cyan-400 hover:text-cyan-300 underline">read the blog</a></p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gray-700/50 rounded-lg p-4 border border-gray-600/50 hover:border-cyan-500/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl flex-shrink-0">{feature.icon}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-sm mb-1">{feature.title}</h3>
                    <p className="text-gray-300 text-xs leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
            <h3 className="text-cyan-400 font-bold text-sm mb-2">💡 Pro Tip</h3>
            <p className="text-gray-300 text-xs leading-relaxed">
              Sign in with Google to unlock cloud sync, setlist management, and jam sessions. 
              Your charts will be automatically backed up and available on all your devices!
            </p>
          </div>
        </div>

        <footer className="border-t border-gray-700 p-4">
          <button
            onClick={onClose}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            Got it, let's create!
          </button>
        </footer>
      </div>
    </div>
  );
};

export default HelpModal;
